import React, { useEffect, useRef, useState } from "react";

type Props = {
  audioId: string;         // id de l'<audio> cible (ex: "audio-methode")
  size?: number;           // diamètre px (def 84)
  label?: string;          // aria-label
  className?: string;      // utilitaires (positions)
  reactive?: boolean;      // léger effet réactif (off par défaut)
};

export default function PlayBadge({
  audioId,
  size = 84,
  label = "Écouter l'essentiel : 5 min",
  className = "",
  reactive = false,
}: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const rafRef = useRef<number | null>(null);
  const ringRef = useRef<HTMLDivElement | null>(null);

  // Écoute des événements du <audio>
  useEffect(() => {
    const el = document.getElementById(audioId) as HTMLAudioElement | null;
    if (!el) return;
    const onPlay = () => { setIsPlaying(true); setIsLoading(false); };
    const onPause = () => setIsPlaying(false);
    const onWaiting = () => setIsLoading(true);
    const onCanPlay = () => setIsLoading(false);
    const onEnded = () => setIsPlaying(false);
    const onError = () => { setHasError(true); setIsPlaying(false); setIsLoading(false); };

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("canplay", onCanPlay);
    el.addEventListener("ended", onEnded);
    el.addEventListener("error", onError);

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("canplay", onCanPlay);
      el.removeEventListener("ended", onEnded);
      el.removeEventListener("error", onError);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [audioId]);

  // Optionnel : micro "réactif" sans son (on lit, on n'émet RIEN)
  useEffect(() => {
    if (!reactive || !isPlaying) return;
    const el = document.getElementById(audioId) as HTMLAudioElement | null;
    const ring = ringRef.current;
    if (!el || !ring) return;
    const Ctx = (window.AudioContext || (window as any).webkitAudioContext);
    if (!Ctx) return;

    let ctx: AudioContext | null = null;
    let analyser: AnalyserNode | null = null;
    let source: MediaElementAudioSourceNode | null = null;
    
    try {
      ctx = new Ctx();
      analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source = ctx.createMediaElementSource(el);
      source.connect(analyser);
      // NE PAS connecter à ctx.destination → pas de son ajouté
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const loop = () => {
        if (!analyser) return;
        analyser.getByteFrequencyData(dataArray);
        const avg = Array.from(dataArray).reduce((a, b) => a + b, 0) / dataArray.length;
        const scale = 1 + Math.min(0.06, avg / 1024);
        ring.style.transform = `scale(${scale.toFixed(3)})`;
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } catch {}

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { source?.disconnect(); analyser?.disconnect(); ctx?.close(); } catch {}
      if (ringRef.current) ringRef.current.style.transform = "scale(1)";
    };
  }, [reactive, isPlaying, audioId]);

  // Toggle robuste : play/pause/reprise SANS délai ni setTimeout
  const onClick = () => {
    const el = document.getElementById(audioId) as HTMLAudioElement | null;
    if (!el) return;
    try {
      setHasError(false);
      if (el.paused || el.ended) {
        if (el.ended) el.currentTime = 0;
        const p = el.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      } else {
        el.pause();
      }
    } finally {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      try { el.focus({ preventScroll: true }); } catch {}
    }
  };

  const dim = `${size}px`;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-controls={audioId}
      aria-pressed={isPlaying}
      className={[
        "relative select-none rounded-full",
        "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgba(255,106,0,0.55)]",
        className,
      ].join(" ")}
      style={{ width: dim, height: dim, WebkitTapHighlightColor: "transparent" }}
    >
      <div
        ref={ringRef}
        className={[
          "absolute inset-0 rounded-full grid place-items-center",
          isPlaying ? "animate-cw-rings" : "animate-cw-breathe",
          isLoading ? "opacity-70" : "opacity-100",
        ].join(" ")}
      >
        <div
          className="rounded-full grid place-items-center bg-[rgba(255,106,0,0.08)] border border-[rgba(255,106,0,0.35)]"
          style={{ width: size * 0.7, height: size * 0.7 }}
        >
          {isPlaying ? (
            <svg width={size * 0.26} height={size * 0.26} viewBox="0 0 24 24" fill="#FF6A00" aria-hidden="true">
              <rect x="6" y="5" width="4" height="14" rx="1.2" />
              <rect x="14" y="5" width="4" height="14" rx="1.2" />
            </svg>
          ) : (
            <svg width={size * 0.26} height={size * 0.26} viewBox="0 0 24 24" fill="#FF6A00" aria-hidden="true">
              <polygon points="8,5 19,12 8,19" />
            </svg>
          )}
        </div>
      </div>
      <span className="absolute inset-0 rounded-full pointer-events-none"
            style={{ boxShadow: "0 0 0 1px rgba(255,106,0,.28)" }} />
      {hasError && (
        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-xs text-red-500">Audio indisponible</span>
      )}
    </button>
  );
}